import { useBooleanState } from '@cian/react-utils';
import { Button } from '@cian/ui-kit';
import { IconFileExcel16, IconFilePrint16 } from '@cian/ui-kit-design-tokens/icons';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { selectProfessionalSearchUrl } from '../../selectors/selectProfessionalSearchUrl';
import {
  DownloadExcelModalRealtors,
  DownloadExcelModalSearchers,
} from '../../serp/components/bottom_print_excel/components/DownloadExcelModal';
import { PrintPopupContainer } from '../../serp/components/bottom_print_excel/print_popup/container';
import { trackDownloadExcelBtnClick, trackPrintOffersClicked } from '../../serp/components/bottom_print_excel/tracking';
import { useDownloadExcelFile } from '../../serp/components/bottom_print_excel/utils/useDownloadExcelFile';
import { requestAuthentication } from 'shared/actions/authentication';
import { Dispatch, IAppState } from 'shared/common/state';
import { selectIsUserAgent } from 'shared/selectors/selectIsUserAgent';

type TClickAction = 'OpenPrintPopup' | 'OpenExcelPopup';

export function PaginationButtonsContainer() {
  const { state: excelPopupOpen, setFalse: closeExcelPopup, setTrue: openExcelPopup } = useBooleanState(false);
  const { state: printPopupOpen, setFalse: closePrintPopup, setTrue: openPrintPopup } = useBooleanState(false);

  const dispatch: Dispatch = useDispatch();

  const profsearchUrl = useSelector(selectProfessionalSearchUrl);
  const isAgent = useSelector(selectIsUserAgent);
  const isAuthenticated = useSelector((state: IAppState) => state.user.isAuthenticated);

  const { error, handleDownloadExcelFile, isLoading, resetError } = useDownloadExcelFile();

  /** Сбросит ошибку при закрытии модалки */
  React.useEffect(() => {
    if (!excelPopupOpen) {
      resetError();
    }
  }, [excelPopupOpen, resetError]);

  const handleOpenExcelPopup = React.useCallback(() => {
    openExcelPopup();
    trackDownloadExcelBtnClick();
  }, [openExcelPopup]);

  const handleOpenPrintPopup = React.useCallback(() => {
    openPrintPopup();
    trackPrintOffersClicked();
  }, [openPrintPopup]);

  const handleClick = (clickAction: TClickAction) => {
    if (clickAction === 'OpenExcelPopup') {
      handleOpenExcelPopup();

      return;
    }

    if (clickAction === 'OpenPrintPopup') {
      handleOpenPrintPopup();

      return;
    }
  };

  const handleOpenAuth = React.useCallback(
    async (e: React.MouseEvent<HTMLButtonElement>, clickAction: TClickAction) => {
      e.stopPropagation();
      if (!isAuthenticated) {
        dispatch(
          requestAuthentication({
            onSuccess: () => () => handleClick(clickAction),
          }),
        );
      } else {
        handleClick(clickAction);
      }
    },
    [isAuthenticated],
  );

  const DownloadExcelModalComponent = isAgent ? DownloadExcelModalRealtors : DownloadExcelModalSearchers;

  return (
    <>
      <Button
        size="XS"
        beforeIcon={<IconFileExcel16 color="icon-main-default" />}
        theme="fill_white_primary"
        onClick={e => handleOpenAuth(e, 'OpenExcelPopup')}
      >
        Сохранить файл Excel
      </Button>

      <Button
        size="XS"
        beforeIcon={<IconFilePrint16 color="icon-main-default" />}
        theme="fill_white_primary"
        onClick={e => handleOpenAuth(e, 'OpenPrintPopup')}
      >
        Распечатать объявления
      </Button>
      <PrintPopupContainer open={printPopupOpen} close={closePrintPopup} />
      <DownloadExcelModalComponent
        isLoading={isLoading}
        isOpened={excelPopupOpen}
        onClose={closeExcelPopup}
        onDownloadClick={handleDownloadExcelFile}
        error={error}
        profsearchUrl={profsearchUrl}
      />
    </>
  );
}
